import {
    ApiCredentialsError,
    ApiDevError,
    ApiGeneralError,
    ApiStandardError,
    ApiTimeoutError,
    ApiUnknownError,
    type ApiErrorMeta,
    type DevErrorInfo,
    type GeneralErrorInfo,
} from "@/modules/api/ApiError.ts";
import { validateSchema } from "@/modules/api/schemaCheck.ts";
import type { ApiErrorResponse, paths } from "@warrenio/api-spec/spec.oats.gen";
import { isObjectWithKey } from "@warrenio/utils/isObject";
import type createClient from "openapi-fetch";
import type { FetchResponse } from "openapi-fetch";
import { isObjectType } from "remeda";
import type { z } from "zod";

//#region Error handling and exceptions

function throwApiException(response: Response, error: unknown): never {
    const meta: ApiErrorMeta = {
        requestUrl: response.url,
        status: response.status,
        correlationId: response.headers.get("x-warren-correlation-id"),
    };

    if (isObjectWithKey(error, "message")) {
        if (response.status === 403) {
            throw new ApiCredentialsError(error as GeneralErrorInfo, meta);
        }

        if ("stack" in error) {
            throw new ApiDevError(error as DevErrorInfo, meta);
        }

        throw new ApiGeneralError(error as GeneralErrorInfo, meta);
    }

    if (isObjectWithKey(error, "errors") && isObjectType(error.errors)) {
        throw new ApiStandardError(error as ApiErrorResponse, meta);
    }

    const errorMessage = typeof error === "string" && error !== "" ? error.slice(0, 250) : response.statusText;
    if (response.status === 504) {
        throw new ApiTimeoutError(error, meta, errorMessage);
    } else {
        throw new ApiUnknownError(error, meta, errorMessage);
    }
}

//#endregion

/** Wrapper interface to debloat IDE tooltips  */
export interface ApiClient extends ReturnType<typeof createClient<paths>> {}

/**
 * Alternate version of openapi-fetch {@link FetchResponse} with the type of `data` directly being represented by the
 * {@link TData} generic type parameter.
 *
 * Needed to be able to infer the type of {@link TData} in other contexts.
 */
export type TypedFetchResponse<TData> =
    | {
          data: TData;
          error?: never;
          response: Response;
      }
    | {
          data?: never;
          error: unknown;
          response: Response;
      };

/** Get the data from an API client fetch response. Throw an error otherwise. */
export function getResponseData<T extends TypedFetchResponse<any>>(fetchResponse: T, schema?: z.ZodTypeAny | false) {
    /* eslint-disable @typescript-eslint/no-unsafe-return -- ESLint does not understand that a subtype of a generic `any` parameter will not be concretely `any` (unless the caller explicitly uses `any`) */
    type ResponseData = Exclude<T["data"], undefined>;

    const { response } = fetchResponse;

    const error: unknown = fetchResponse.error;
    const data: unknown = fetchResponse.data;

    // Check for empty response
    if (
        response.ok &&
        data === undefined &&
        (response.headers.get("content-type")?.startsWith("text/plain") || response.status === 204)
    ) {
        console.info("API response is OK but data is undefined: %o", response);
        // Hopefully the API spec allows for this...
        return undefined as ResponseData;
    }

    // NB: `error` can be empty for some 500 internal server errors
    if (error !== undefined || data === undefined) {
        console.info("API error response: %o", fetchResponse);
        throwApiException(response, error);
    }

    if (import.meta.env.DEV && schema) {
        validateSchema(response, schema, data);
    }

    return data as ResponseData;
    /* eslint-enable @typescript-eslint/no-unsafe-return */
}
